import { type DiscountedPrice } from '@commercetools/platform-sdk'
import { useLoaderData, useLocation } from '@remix-run/react'
import React, { useState, useEffect, useRef, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import VisitorHistorySlider from '#app/components/common/clerk-sliders/visitor-history'
import PlentyBody from '#app/components/typography/plenty-body'
import { useUtmParams } from '#app/hooks/useUtmParams'
import { type loader } from '#app/routes/_public+/product+/$styleNumber'
import { trackPdpViewed } from '#app/utils/tracking/amplitude/pdp-viewed'
import { SendViewContentMetaPixelEvent } from '#app/utils/tracking/meta/view-content-pdp'
import { type Outfit } from '#types/outfit.ts'
import {
	type StyleVariant,
	type PlentyVariant,
	type PlentyVariantAvailability,
} from '#types/product'
import ShopTheLookPDPCarousel from '../shop-the-look/carousel/shop-the-look-pdp-carousel'
import BagItem from './components/bag-item-v2'
import ClerkProductsVerticalTwoByTwoSimilarProducts from './components/carousel-variants/clerk-carousel-mobile-pdp-vertical'
import ClerkCarouselSuggestion from './components/carousel-variants/clerk-carousel-suggestion'
import ComplementProducts from './components/complement-products'
import ProductActions, {
	getBadgeForSelectItem,
} from './components/product-actions'
import ProductBreadcrumbs from './components/product-breadcrumbs'
import ProductGallery from './components/product-gallery'
import ProductInformation from './components/product-information'
import ProductOverview from './components/product-overview'
import ProductPerksAndAvailability from './components/product-perks-and-availability'
import StoreSelectorPDP from './components/store-selector-pdp'

export const sizeGuidesURL =
	'https://storage.googleapis.com/bucket-plenty-dev/BrandSizeGuides'

const ProductDetailsPage = () => {
	let { t } = useTranslation('product_details_page')
	const loaderData = useLoaderData<typeof loader>()
	const utmParams = useUtmParams()
	const productData = loaderData?.productData
	const [selectedSku, setSelectedSku] = useState<string | null>(
		loaderData.skuParam,
	)
	const location = useLocation()
	const { pathname, search } = location
	const [currentImages, setCurrentImages] = useState([])
	const [selectSizeOpen, setSelectSizeOpen] = useState<boolean>(false)
	const amplitudePageSource = location.state?.amplitudePageSource
	const lastSentSkuRef = useRef('')
	const lastSentSkuMeta = useRef('')
	const activeStyle =
		productData?.styleAvailableVariants.find(it => it.active) ||
		productData?.styleAvailableVariants[0]

	const sizeOptions = useMemo(() => {
		return (
			activeStyle?.variants.map(sizeVariant => {
				const size = sizeVariant.attributes?.find(item => item.name === 'size')
					?.value
				const length = sizeVariant.attributes?.find(
					item => item.name === 'length',
				)?.value
				const isOutOfStock = !sizeVariant.availability?.find(it => it.isOnStock)
				const isPriceMissingChannel = !sizeVariant.prices?.some(item => {
					return item.channel?.id
				})
				const channelFromStoresParam = loaderData?.storeParams.length
					? sizeVariant.availability?.find(
							it => it.storeKey && loaderData.storeParams.includes(it.storeKey),
					  )?.channelKey
					: undefined

				const discountedPrice = loaderData?.storeParams.length
					? channelFromStoresParam === sizeVariant.lowestPrice.channel?.id
						? sizeVariant.lowestPrice.discounted
						: undefined
					: sizeVariant.lowestPrice.discounted

				return {
					label: length ? `${size} / ${length.replace('"', '')}` : `${size}`,
					value: sizeVariant.sku!,
					disabled: isOutOfStock || isPriceMissingChannel,
					badgeProps: getBadgeForSelectItem(
						discountedPrice as DiscountedPrice,
						isOutOfStock,
						sizeVariant.availability?.reduce((total, availability) => {
							const quantity = (availability.availableQuantity as number) || 0
							return total + quantity
						}, 0) || 0,
						t,
					),
				}
			}) || []
		)
	}, [activeStyle, loaderData?.storeParams, t])

	useEffect(() => {
		if (activeStyle && activeStyle.lowestPrice && productData) {
			const skuToSend = activeStyle?.variants?.[0]?.sku

			// Only send if it's a new SKU to avoid duplicate sends
			if (lastSentSkuMeta.current !== skuToSend) {
				lastSentSkuMeta.current = skuToSend

				const price =
					activeStyle.lowestPrice.value?.centAmount !== undefined
						? activeStyle.lowestPrice.value.centAmount / 100
						: 0
				const currency = activeStyle.lowestPrice.value?.currencyCode || 'DKK'

				const metaPixelData = {
					content_category: productData?.productInfo?.categories?.[0]?.obj?.slug
						?.en
						? (() => {
								const categorySlug =
									productData.productInfo.categories[0].obj.slug.en
								const parts = categorySlug.split('-')
								return parts.length === 5
									? parts[parts.length - 2]
									: parts.pop() || 'unknown_category'
						  })()
						: 'unknown_category',
					content_ids: [skuToSend],
					content_name: productData?.name || 'unknown_name',
					content_type: 'product_group',
					currency,
					value: price,
					eventID: `sh-${new Date().getTime()}`,
				}
				SendViewContentMetaPixelEvent(metaPixelData)
			}
		}
	}, [location.pathname, location.search, activeStyle, productData])

	useEffect(() => {
		// Track PDP view event with Amplitude
		trackPdpViewed(
			productData,
			activeStyle,
			selectedSku,
			amplitudePageSource,
			utmParams,
			sizeOptions,
			lastSentSkuRef,
		)
	}, [
		pathname,
		search,
		productData,
		selectedSku,
		utmParams,
		amplitudePageSource,
		activeStyle,
		sizeOptions,
	])

	const selectedVariant = activeStyle?.variants.find(
		variant => variant.sku === selectedSku,
	)
	const variantWithAvailability =
		activeStyle?.variants.find(variant => {
			if (variant && variant.availability) {
				return variant.availability.find(av => av.isOnStock && av.storeKey)
			} else {
				return false
			}
		}) || activeStyle?.variants[0]

	// Create a consolidated list of all available stores when no variant is selected
	const getAllStoresFromGeneralAvailability = () => {
		const storeMap = new Map<string, PlentyVariantAvailability>()

		if (activeStyle?.generalAvailability?.bySize) {
			// Go through each size
			Object.values(activeStyle.generalAvailability.bySize).forEach(
				sizeInfo => {
					// For each size, go through each store availability
					if (sizeInfo.storeAvailability) {
						sizeInfo.storeAvailability.forEach(store => {
							if (store.quantity > 0) {
								// If this store is not yet in our map, add it
								if (!storeMap.has(store.storeKey)) {
									const storeAvailability: PlentyVariantAvailability = {
										storeKey: store.storeKey,
										storeName: store.storeName,
										isOnStock: true,
										isOnSale: store.isOnSale,
										availableQuantity: store.quantity,
										channelKey: store.storeKey, // Assuming channelKey matches storeKey
										storeAddress: store.storeAddress, // Include the store address
										geoLocation: store.geoLocation, // Include the geo location
									}
									storeMap.set(store.storeKey, storeAvailability)
								} else {
									// If the store is already in our map, update its quantity
									const existingStore = storeMap.get(store.storeKey)
									if (existingStore) {
										existingStore.availableQuantity =
											(existingStore.availableQuantity || 0) + store.quantity
									}
								}
							}
						})
					}
				},
			)
		}

		// Convert the map values to an array
		return Array.from(storeMap.values())
	}

	// Use the consolidated store list when no variant is selected
	const firstAvailability = selectedVariant
		? selectedVariant.availability?.filter(av => av.isOnStock && av.storeKey)
		: activeStyle?.generalAvailability
		  ? getAllStoresFromGeneralAvailability()
		  : variantWithAvailability?.availability?.filter(
					av => av.isOnStock && av.storeKey,
		    )

	if (!activeStyle) return null
	return (
		<>
			<StoreSelectorPDP />
			<div className="mb-14 flex min-h-screen flex-col gap-y-14 md:mb-28 md:gap-y-28">
				<div className="m-0 flex w-full flex-col gap-2 md:gap-y-4 md:px-[var(--plenty-padding)]">
					<div className="flex flex-col px-4 md:px-0">
						<ProductBreadcrumbs />
						<BagItem />
					</div>
					<div className="flex w-full grow flex-wrap md:flex-nowrap ">
						{/* Left Column for Images */}
						<ProductGallery
							activeStyle={activeStyle as StyleVariant}
							images={currentImages}
						/>
						{/* Right Column for Product Details */}
						<div className="flex w-full flex-col gap-2 px-4 md:w-1/2 md:px-0 md:pl-10">
							<ProductOverview
								selectedSku={selectedSku || ''}
								setSelectedSku={setSelectedSku}
								setCurrentImages={setCurrentImages}
							/>
							<ProductActions
								setSelectedSku={setSelectedSku}
								selectedSku={selectedSku}
								soldOut={!activeStyle.isOnStock}
								completelySoldOut={
									!activeStyle.isAvailableInSelectedStore &&
									!activeStyle.isAvailableInOtherStores
								}
								firstAvailability={
									firstAvailability as PlentyVariantAvailability[]
								}
								selectSizeOpen={selectSizeOpen}
								setSelectSizeOpen={setSelectSizeOpen}
							/>
							<ProductPerksAndAvailability
								firstAvailability={
									firstAvailability as PlentyVariantAvailability[]
								}
								selectedSku={selectedSku}
								setSelectSizeOpen={setSelectSizeOpen}
							/>

							{/* Accordion for Description, Size & Fit, etc. */}
							<ProductInformation />
							<ComplementProducts activeStyle={activeStyle as StyleVariant} />
							{loaderData.outfitsByStyle &&
								Array.isArray(loaderData.outfitsByStyle) &&
								(loaderData.outfitsByStyle as unknown as Outfit[]).length >
									0 && (
									<div className="flex w-full flex-col gap-2 md:hidden">
										<PlentyBody className="font-semibold" size="lg">
											{t('shop_our_instagram_looks')}
										</PlentyBody>

										<ShopTheLookPDPCarousel
											outfitsByStyle={
												loaderData.outfitsByStyle as unknown as Outfit[]
											}
										/>
									</div>
								)}
						</div>
						<div className="flex flex-col py-4 md:hidden">
							<ClerkProductsVerticalTwoByTwoSimilarProducts />
						</div>
						<div className="ml-0 flex w-full pl-4 pt-4 md:hidden">
							<VisitorHistorySlider />
						</div>
					</div>
				</div>

				<div className="hidden md:flex md:flex-col">
					<ClerkCarouselSuggestion />
				</div>
			</div>
		</>
	)
}

export default ProductDetailsPage

export const findVariantBySku = (variants: PlentyVariant[], sku: string) => {
	return variants.find(variant => {
		return variant.sku === sku
	})
}
